import * as uuid from 'uuid';
import actions from './actions';
import { createReducer } from '../../../helpers/reducer';
import { keyBy } from '../../../helpers/core';

const initState = {
  loading: false,
  isCreating: false,
  isDeleting: false,
  editForm: undefined,
  cancellationPolicies: undefined,
  isCancellationPolicyExists: false,
};

export const initialItem = () => {
  return {
    internalKey: uuid.v4(),
    id: null,
    min_talents: 0,
    max_talents: 0,
    min_period: 0,
    max_period: 0,
    value: 0,
    _destroy: false,
  };
};

const initialEditData = () => {
  return {
    cancellation_policies: [initialItem()],
  };
};

function validate() {
  return '';
}

function formatObject(payload, prop, state) {
  const propsArray = Object.keys(payload[prop]);

  return keyBy(
    propsArray,
    name => name,
    // eslint-disable-next-line no-use-before-define
    name => formatProp(payload[prop], name, state),
  );
}

function formatArray(payload, prop, state) {
  // eslint-disable-next-line no-use-before-define
  return payload[prop].map((item, index) => formatProp(payload[prop], index, state));
}

function formatProp(payload, prop, state) {
  const getProp = () => ({
    name: prop,
    value: payload[prop],
    error: validate(prop, payload[prop], state),
    touched: false,
  });

  if (Array.isArray(payload[prop])) {
    return formatArray(payload, prop, state);
  }

  if (!Array.isArray(payload[prop]) && typeof payload[prop] === 'object' && payload[prop] != null) {
    return formatObject(payload, prop, state);
  }

  return getProp();
}

function onCreateInitialForm(state) {
  const init = initialEditData();

  const propsArray = Object.keys(init);
  const editForm = keyBy(
    propsArray,
    prop => prop,
    prop => formatProp(init, prop, state),
  );

  return { ...state, editForm, loading: false };
}

function onCreateEditForm(state) {
  const cancellation_policies = {
    cancellation_policies: state.cancellationPolicies.map(object => ({
      id: object.id.value,
      max_period: object.max_period.value,
      max_talents: object.max_talents.value,
      min_period: object.min_period.value,
      min_talents: object.min_talents.value,
      unit: object.unit.id,
      value: object.value.value,
    })),
  };

  const propsArray = Object.keys(cancellation_policies);
  const editForm = keyBy(
    propsArray,
    prop => prop,
    prop => formatProp(cancellation_policies, prop, state),
  );

  return { ...state, editForm };
}

function onLoadRequest(state) {
  return {
    ...state,
    loading: true,
  };
}

function onLoadSuccess(state, { payload }) {
  const { cancellationPolicyItems } = payload;
  const cancellation_policies = { cancellation_policies: cancellationPolicyItems };

  const propsArray = Object.keys(cancellation_policies);
  const editForm = keyBy(
    propsArray,
    prop => prop,
    prop => formatProp(cancellation_policies, prop, state),
  );

  const cancellationPolicies = editForm.cancellation_policies.map(object => ({ ...object })); // For display only

  return {
    ...state,
    editForm,
    cancellationPolicies,
    isCancellationPolicyExists: !!cancellationPolicyItems?.length,
    loading: false,
  };
}

function onLoadError(state) {
  return {
    ...state,
    loading: false,
  };
}

function getValue(attrs, value, state, recordIndex) {
  if (!Array.isArray(value) && typeof value === 'object' && value != null) {
    return keyBy(
      Object.keys(value),
      prop => prop,
      prop => formatProp(value, prop, state),
    );
  }

  return {
    name: attrs,
    touched: true,
    error: validate(attrs, value, state, recordIndex),
    value: Array.isArray(value) ? value.map((item, index) => formatProp(value, index, state)) : value,
  };
}

function applyErrors(errors, editForm) {
  Object.keys(errors).forEach(key => {
    // Ensure that the corresponding index exists in array editFormWithErrors
    if (editForm[key]) {
      // Dynamically update all keys in object 'errors' for the corresponding index in editForm
      Object.keys(errors[key]).forEach(field => {
        if (editForm[key][field]) {
          [editForm[key][field].error] = errors[key][field];
          editForm[key][field].touched = true;
        }
      });
    }
  });

  return editForm;
}

function onChangeNestedProperty(state, { payload }) {
  const { attrs, value } = payload;
  const editForm = { ...state.editForm };

  if (attrs.length > 2) {
    let data = null;
    const name = attrs[attrs.length - 1];
    for (let i = 0; i < attrs.length - 1; i += 1) {
      data = (data || editForm)[attrs[i]];
    }

    data[name] = getValue(name, value, state, attrs[attrs.length - 2]);
  } else if (attrs.length === 2) {
    if (editForm[attrs[0]] === undefined) {
      editForm[attrs[0]] = {};
    }
    editForm[attrs[0]][attrs[1]] = getValue(attrs, value, state, attrs[attrs.length - 2]);
  } else {
    editForm[attrs[0]] = getValue(attrs, value, state, attrs[attrs.length - 2]);
  }

  return { ...state, editForm };
}

function onCreateRequest(state) {
  return {
    ...state,
    isCreating: true,
  };
}

function onCreateSuccess(state) {
  return {
    ...state,
    isCreating: false,
  };
}

function onCreateError(state, { payload }) {
  const { data } = payload;

  const errors = data.cancellation_policies;
  const editFormWithErrors = applyErrors(errors, state.editForm.cancellation_policies);

  return {
    ...state,
    editForm: { cancellation_policies: editFormWithErrors },
    isCreating: false,
  };
}

function onEditRequest(state) {
  return {
    ...state,
    isCreating: true,
  };
}

function onEditSuccess(state) {
  return {
    ...state,
    isCreating: false,
  };
}

function onEditError(state, { payload }) {
  const { data } = payload;

  const errors = data?.cancellation_policies || {};
  const editFormWithErrors = applyErrors(errors, state.editForm.cancellation_policies);

  return {
    ...state,
    editForm: { cancellation_policies: editFormWithErrors },
    isCreating: false,
  };
}

function onDeleteRequest(state) {
  return {
    ...state,
    isDeleting: true,
  };
}

function onDeleteSuccess(state) {
  return {
    ...state,
    isDeleting: false,
  };
}

function onDeleteError(state) {
  return {
    ...state,
    isDeleting: false,
  };
}

function onClearStore() {
  return { ...initState };
}

export default createReducer(initState, {
  [actions.LOAD_REQUEST]: onLoadRequest,
  [actions.LOAD_SUCCESS]: onLoadSuccess,
  [actions.LOAD_ERROR]: onLoadError,
  [actions.CREATE_REQUEST]: onCreateRequest,
  [actions.CREATE_SUCCESS]: onCreateSuccess,
  [actions.CREATE_ERROR]: onCreateError,
  [actions.EDIT_REQUEST]: onEditRequest,
  [actions.EDIT_SUCCESS]: onEditSuccess,
  [actions.EDIT_ERROR]: onEditError,
  [actions.CHANGE_NESTED_PROPERTY]: onChangeNestedProperty,
  [actions.CREATE_INITIAL_FORM]: onCreateInitialForm,
  [actions.CREATE_EDIT_FORM]: onCreateEditForm,
  [actions.DELETE_REQUEST]: onDeleteRequest,
  [actions.DELETE_SUCCESS]: onDeleteSuccess,
  [actions.DELETE_ERROR]: onDeleteError,
  [actions.CLEAR_STORE]: onClearStore,
});
